// apps/roleforge-app/src/components/invitations/InlineInviteAffordance.tsx
// Plan 3e-ii Task 7.2 — slim button + modal beneath an agent message that
// emits a `[INVITE_AFFORDANCE]` token. Clicking opens a small dialog (email
// required, optional note) which calls `inviteTeammateInline`. On 402, the
// CapExceededModal `kind="invite"` variant is surfaced (Task 7.6) with
// activation-trial copy.

import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { inviteTeammateInline } from '../../lib/api-client';
import { parseCapError, type CapError } from '../../lib/cap-error';
import { CapExceededModal } from '../cap/CapExceededModal';

interface Props {
  auditId: string;
}

export function InlineInviteAffordance({ auditId }: Props) {
  const { jwt } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capExceeded, setCapExceeded] = useState(false);
  // Issue #1324: keep the parsed 402 so the modal can state the real org-wide
  // slot numbers instead of a per-audit guess.
  const [capError, setCapError] = useState<CapError | null>(null);

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!jwt || !email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await inviteTeammateInline(jwt, auditId, {
        email: email.trim(),
        note: note.trim() || undefined,
      });
      setOpen(false);
      setEmail('');
      setNote('');
    } catch (err: unknown) {
      // Any cap / 402 response surfaces the invite-cap modal (Task 7.6).
      // The activation-trial CTA inside is the only allowed redirect.
      const cap = parseCapError(err);
      const is402 =
        err && typeof err === 'object' && (err as { status?: number }).status === 402;
      if (cap || is402) {
        setCapError(cap);
        setCapExceeded(true);
        setOpen(false);
      } else {
        const message = err instanceof Error ? err.message : 'Failed to send invitation';
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center text-xs font-semibold text-plum underline underline-offset-2 hover:text-ink"
      >
        Invite a teammate
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="inline-invite-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <form
            onSubmit={handleSubmit}
            className="relative bg-paper rounded-2xl shadow-soft max-w-md w-full mx-4 p-6 flex flex-col gap-3"
          >
            <h3 id="inline-invite-title" className="text-base font-semibold text-ink">
              Invite a teammate
            </h3>
            <div className="flex flex-col gap-1">
              <label htmlFor="inline-invite-email" className="text-sm font-medium text-ink">
                Email
              </label>
              <input
                id="inline-invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@company.com"
                className="rounded-xl border border-cloud px-4 py-2 text-sm text-ink placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-plum"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="inline-invite-note" className="text-sm font-medium text-ink">
                Note <span className="text-graphite font-normal">(optional)</span>
              </label>
              <textarea
                id="inline-invite-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Why you're asking them, in your words."
                className="rounded-xl border border-cloud px-4 py-2 text-sm text-ink placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-plum"
              />
              {/* Issue #1324: this note is now really delivered — it is stored on
                  the invitation and quoted in the invitation email. */}
              <p className="text-xs text-graphite">Quoted in their invitation email.</p>
            </div>
            {error && (
              <p role="alert" className="text-sm text-ember">
                {error}
              </p>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={close}
                className="text-sm text-graphite hover:text-ink transition px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="inline-flex items-center justify-center rounded-xl bg-plum text-paper px-4 py-2 text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
              >
                {busy ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      )}

      {capExceeded && (
        <CapExceededModal
          kind="invite"
          auditId={auditId}
          error={capError ?? undefined}
          onClose={() => {
            setCapExceeded(false);
            setCapError(null);
          }}
        />
      )}
    </>
  );
}
